<template>
  <div class="search-actions row" :class="{ desktop: $isDesktop }">
    <pu-search-link
      v-if="showMap"
      :hierarchy-path="hierarchyPath"
      :custom-query-params="mapUrlPlaceNameQuery"
      category="search"
      exclude-type="visibleMarker"
    >
      <img
        src="./list.svg"
        class="list-icon"
        :alt="$t('List')"
        width="22"
        height="14"
      />
      <translate class="text"> List </translate>
    </pu-search-link>
    <pu-search-link
      v-else
      :hierarchy-path="hierarchyPath"
      category="maps"
      :custom-query-params="mapUrlQueryParams"
      :aria-label="$t('Map')"
      exclude-type="searchMap"
      data-test="map-search-link"
    >
      <img src="./map.svg" alt="$t('Map')" width="21" height="18" />
      <translate class="text"> Map </translate>
    </pu-search-link>

    <filter-panel :selected-hierarchy-name="selectedHierarchyName" />
  </div>
</template>

<script lang="ts">
import { ensureArray } from '~/utility/array'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { SearchFiltersUrl } from '../../url/search-filters/SearchFiltersUrl'

@Component({
  setup() {
    const searchWizardStore = useSearchWizardStore(usePinia())
    const searchStore = useSearchStore(usePinia())
    return { searchWizardStore, searchStore }
  },
})
class SearchNavBar extends Vue {
  searchWizardStore: ReturnType<typeof useSearchWizardStore>
  searchStore: ReturnType<typeof useSearchStore>

  get searchFilters() {
    return this.searchStore.searchFilters
  }

  @Prop({ required: true })
  resultsCount: number

  @Prop({ default: false, type: Boolean })
  showMap: boolean

  @Prop({ required: false })
  visibleMarker?: { slug: string; point: string | null }

  @Prop({ required: false })
  mapCentre?: string

  @Prop()
  selectedHierarchyName: string

  get placename() {
    return ensureArray(this.$route.query.placename)[0] || undefined
  }

  onFilterCancel() {
    this.searchWizardStore.panelOpen = false
  }

  get mapUrlQueryParams() {
    return {
      facet: this.searchFilters.facets,
      ...this.mapUrlVisibleMarkerQuery,
      ...this.mapUrlPlaceNameQuery,
    }
  }

  get mapUrlVisibleMarkerQuery() {
    return this.visibleMarker
      ? {
          visibleMarker: this.visibleMarker.slug,
        }
      : {}
  }

  get mapUrlPlaceNameQuery() {
    const placename = ensureArray(this.$route.query.placename)[0] || undefined
    return placename ? { placename } : {}
  }

  get filterAndSortUrlQuery() {
    const route = this.searchFilters
      ? SearchFiltersUrl.filtersToUrl(this.searchFilters).route
      : { query: {} }
    return {
      ...route.query,
      hierarchyPath: this.hierarchyPath,
      targetUrl: this.showMap ? 'maps' : 'search',
    }
  }

  get hierarchyPath() {
    return this?.searchFilters?.hierarchyPath
      ? this.searchFilters.hierarchyPath
      : ''
  }
}

export default toNative(SearchNavBar)
</script>

<style scoped>
.search-actions {
  background-color: white;
  border-top: 1px solid #dedede;
  height: 13vw;
  width: 100vw;
}

a {
  align-items: center;
  display: flex;
  flex-grow: 1;
  font-size: 1em;
  justify-content: center;
  padding: 0.75em;
  text-decoration: none;
}

a:active {
  transform: translateY(0);
  background: rgba(67, 89, 125, 0.4);
}

a:first-of-type {
  border-right: 1px solid #ccc;
}

.text {
  color: var(--colour-text-default);
  font-weight: normal;
  line-height: 1;
  text-align: left;
  text-decoration: none;
}

img {
  height: 1.1em;
  margin-right: 0.5em;
  width: auto;
}

.list-icon {
  height: 1em;
}

/* mobile landscape */
@media screen and (min-width: 320px) and (max-width: 998px) and (orientation: landscape) {
  .search-actions {
    height: 2.5em;
  }
}

/* desktop & tablet */
.search-actions.desktop {
  border-top: none;
  height: auto;
  width: auto;
  justify-content: space-between;
  flex-direction: row-reverse;
  padding: 0 1em;
}

.search-actions.desktop {
  height: auto;
}

.search-actions.desktop a {
  flex-grow: 0;
}

.desktop a:first-of-type {
  border-right: none;
}
</style>
